// components/PropertyList.jsx
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProperties, deleteProperty } from "../redux/slices/propertySlice";
import EditProperty from "./EditProperty";

const AVAILABLE_FOR_OPTIONS = [
  "family", "bachelor_boy", "bachelor_girl", "only_boys", "only_girls", "all",
];

const formatLabel = (str) =>
  str ? str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

export default function PropertyList() {
  const dispatch = useDispatch();
  const { properties, pagination, loading, error } = useSelector((s) => s.property);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [availableFor, setAvailableFor] = useState("");
  const [page, setPage] = useState(1);
  const [editingProperty, setEditingProperty] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, availableFor]);

  useEffect(() => {
    dispatch(fetchProperties({
      page,
      limit: 10,
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(availableFor && { availableFor }),
    }));
  }, [dispatch, page, debouncedSearch, availableFor]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    await dispatch(deleteProperty(id));
    setDeletingId(null);
    setConfirmDeleteId(null);
  };

  const totalPages = pagination?.pages || 1;

  return (
    <div className="container-fluid py-3">

      {/* Edit Modal */}
      {editingProperty && (
        <EditProperty
          property={editingProperty}
          onClose={() => setEditingProperty(null)}
        />
      )}

      {/* Delete Confirm Modal */}
      {confirmDeleteId && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.45)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Delete Property</h6>
                <button className="btn-close" onClick={() => setConfirmDeleteId(null)} />
              </div>
              <div className="modal-body">
                Are you sure you want to delete this property? This cannot be undone.
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDeleteId(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  disabled={!!deletingId}
                  onClick={() => handleDelete(confirmDeleteId)}
                >
                  {deletingId ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">Property Listings</h5>
        <span className="text-muted small">{pagination?.total ?? 0} total</span>
      </div>

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-md-5">
          <div className="input-group input-group-sm">
            <span className="input-group-text">🔍</span>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name or city..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="btn btn-outline-secondary" onClick={() => setSearch("")}>✕</button>
            )}
          </div>
        </div>
        <div className="col-md-3">
          <select
            className="form-select form-select-sm"
            value={availableFor}
            onChange={(e) => setAvailableFor(e.target.value)}
          >
            <option value="">All — Available For</option>
            {AVAILABLE_FOR_OPTIONS.map((o) => (
              <option key={o} value={o}>{formatLabel(o)}</option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => { setSearch(""); setAvailableFor(""); setPage(1); }}
          >
            Reset
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {error && <div className="alert alert-danger m-3 mb-0">{error}</div>}
        <div className="table-responsive">
          <table className="table table-sm table-bordered table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Location</th>
                <th>Rent (₹/mo)</th>
                <th>Available For</th>
                <th>Furnishing</th>
                <th>Area</th>
                <th>Security Dep.</th>
                <th>Rent Agr.</th>
                <th>Age (yr)</th>
                <th>Amenities</th>
                <th>Featured</th>
                <th>Available</th>
                <th>Rating</th>
                <th>Views</th>
                <th>Owner</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(6)].map((_, i) => (
                  <tr key={i} className="placeholder-glow">
                    {[...Array(16)].map((__, j) => (
                      <td key={j}><span className="placeholder col-10 rounded" /></td>
                    ))}
                  </tr>
                ))
              ) : properties.length === 0 ? (
                <tr>
                  <td colSpan={16} className="text-center text-muted py-4">
                    No properties found.
                  </td>
                </tr>
              ) : (
                properties.map((p, i) => (
                  <tr key={p._id}>
                    <td className="text-muted">{(page - 1) * 10 + i + 1}</td>

                    <td>
                      <div className="fw-medium">{p.name}</div>
                      {p.description && (
                        <small className="text-muted" title={p.description}>
                          {p.description.length > 40
                            ? p.description.slice(0, 40) + "…"
                            : p.description}
                        </small>
                      )}
                    </td>

                    <td>
                      <div>{p.location?.city || "—"}</div>
                      <small className="text-muted">
                        {[p.location?.state, p.location?.country].filter(Boolean).join(", ")}
                      </small>
                    </td>

                    <td>₹{Number(p.rentAmount || 0).toLocaleString("en-IN")}</td>

                    <td>
                      <span className="badge bg-secondary bg-opacity-10 text-dark border">
                        {formatLabel(p.availableFor)}
                      </span>
                    </td>

                    <td>
                      <span className={`badge ${p.furnishing === "fully_furnished"
                          ? "bg-success"
                          : p.furnishing === "semi_furnished"
                            ? "bg-warning text-dark"
                            : "bg-light text-dark border"
                        }`}>
                        {formatLabel(p.furnishing)}
                      </span>
                    </td>

                    <td>{p.area ? `${p.area} sqft` : "—"}</td>

                    <td>
                      {p.securityDeposit
                        ? `₹${Number(p.securityDeposit).toLocaleString("en-IN")}`
                        : "—"}
                    </td>

                    <td>{p.rentAgreement ? `${p.rentAgreement} mo` : "—"}</td>

                    <td>{p.propertyAge ?? "—"}</td>

                    <td>
                      {p.amenities?.length > 0 ? (
                        <span title={p.amenities.join(", ")}>
                          {p.amenities.slice(0, 2).join(", ")}
                          {p.amenities.length > 2 && (
                            <span className="text-muted"> +{p.amenities.length - 2}</span>
                          )}
                        </span>
                      ) : "—"}
                    </td>

                    <td className="text-center">
                      {p.isFeatured
                        ? <span className="badge bg-warning text-dark">Yes</span>
                        : <span className="text-muted">No</span>}
                    </td>

                    <td className="text-center">
                      {p.isAvailable
                        ? <span className="badge bg-success">Yes</span>
                        : <span className="badge bg-danger">No</span>}
                    </td>

                    <td>
                      {p.averageRating > 0
                        ? `⭐ ${p.averageRating.toFixed(1)} (${p.totalReviews})`
                        : "—"}
                    </td>

                    <td>{p.views ?? 0}</td>

                    <td>{p.owner?.fullName ?? "—"} <br/>
                        <span className="text-sm">{p.owner?.phone ?? "—"}</span>
                    </td>

                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          title="Edit"
                          onClick={() => setEditingProperty(p)}
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          title="Delete"
                          onClick={() => setConfirmDeleteId(p._id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer d-flex justify-content-between align-items-center py-2">
            <small className="text-muted">Page {page} of {totalPages}</small>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(1)}>«</button>
              </li>
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p - 1)}>‹</button>
              </li>
              {buildPageRange(page, totalPages).map((n, i) =>
                n === "…" ? (
                  <li key={`e${i}`} className="page-item disabled">
                    <span className="page-link">…</span>
                  </li>
                ) : (
                  <li key={n} className={`page-item ${n === page ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setPage(n)}>{n}</button>
                  </li>
                )
              )}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p + 1)}>›</button>
              </li>
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage(totalPages)}>»</button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function buildPageRange(current, total) {
  const range = [];
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    range.push(i);
  }
  if (current - 1 > 2) range.unshift("…");
  if (current + 1 < total - 1) range.push("…");
  range.unshift(1);
  if (total > 1) range.push(total);
  return range;
}
